import React from 'react';
import { Link } from 'react-router-dom';
import LearnLayout from '../LearnLayout';
import { findTopic, neighbors } from '../topics';
import styles from '../Learn.module.css';

const AiScreener: React.FC = () => {
  const topic = findTopic('ai-screener')!;
  const { prev, next } = neighbors(topic.slug);
  return (
    <LearnLayout title={topic.title} tagline={topic.blurb} readTime={topic.readTime} prev={prev} next={next}>
      <p>
        Most AI trading tools wrap a single model in a web UI and call it done. The SignalDeck Screener takes a
        different angle: two different AI models look at the same setup independently, and we only surface candidates
        where they agree. Disagreement is itself a signal.
      </p>

      <h2>The data a Screener pass actually assembles</h2>
      <p>For every ticker you scan, before we ever call an AI, we build a dossier of technical + contextual inputs:</p>
      <ul>
        <li>
          <strong>Technical:</strong> RSI(2), RSI(6), RSI(14), MACD histogram, Bollinger %B, ATR, 20-day range position,
          EMA(9/21/50) trend alignment, relative volume vs 20-day average.
        </li>
        <li>
          <strong>Intraday overlay:</strong> 1-hour RSI + EMA trend, alignment between daily and hourly (a setup that
          works on the daily but is diverging on the hourly gets deprioritized).
        </li>
        <li>
          <strong>Options intel:</strong> ATM implied volatility, put/call ratio, nearest expiry — helps distinguish
          "cheap premium to buy calls" from "sell-premium-only" regimes.
        </li>
        <li>
          <strong>Short interest:</strong> % of float short, days to cover, change since last report. Shapes the
          asymmetric-long vs asymmetric-short lens on the same price chart.
        </li>
        <li>
          <strong>Earnings proximity:</strong> hard block on names within 3 trading days of a report.
        </li>
        <li>
          <strong>Setup history:</strong> how this exact signal has performed historically on this ticker.
        </li>
        <li>
          <strong>Macro / regime:</strong> current market mood, geopolitical risk, sector bias, the day's trading
          playbook.
        </li>
      </ul>

      <h2>The two AI passes</h2>
      <h3>1. Claude Haiku — the analyst</h3>
      <p>
        Claude gets the full dossier plus the recent-calibration block (see the{' '}
        <Link to="/learn/calibration">Calibration</Link> explainer) and returns a structured verdict:
      </p>
      <ul>
        <li>
          <code>direction</code> — LONG / SHORT / AVOID
        </li>
        <li>
          <code>confidence</code> — 0-100
        </li>
        <li>
          <code>reason</code> — 2-3 sentence explanation citing which indicators drove the call
        </li>
        <li>
          <code>stop_loss</code> and <code>take_profit</code> — the prices that would invalidate or fulfill the thesis
        </li>
        <li>
          <code>hold_days</code> and <code>profit_target_pct</code>
        </li>
      </ul>
      <p>
        <strong>Claude is allowed — and often correct — to say AVOID.</strong> The prompt leads with trading discipline
        rules specifically designed to kill the "always something to trade" failure mode that plagues most AI screeners.
      </p>

      <h3>2. GPT-4o-mini — the second opinion</h3>
      <p>
        GPT receives Claude's verdict + reason and returns its own AGREE / PARTIALLY_AGREE / DISAGREE judgment. This is
        deliberately a <em>different</em> model with a <em>different</em> training distribution — if they both conclude
        the same thing from the same data, that's a stronger signal than either alone.
      </p>

      <h2>The consensus tag is the headline</h2>
      <div className={styles.example}>
        <div className={styles.label}>What you actually see on a result row</div>
        <ul>
          <li>
            <strong>BOTH_AGREE</strong> — Claude and GPT both picked the same direction with ≥ 60% confidence. This is
            the highest-conviction signal the Screener produces.
          </li>
          <li>
            <strong>LEAN_AGREE</strong> — Both picked the same direction but one is lukewarm. A valid trade, but treat
            it as smaller-size.
          </li>
          <li>
            <strong>CLAUDE_ONLY</strong> — Claude made a directional call; GPT disagreed or said HOLD. Real-world win
            rates here are meaningfully lower than BOTH_AGREE. Worth watching, not worth auto-executing.
          </li>
        </ul>
      </div>

      <h2>What the confidence number does not mean</h2>
      <div className={styles.calloutWarn}>
        <strong>A 75% confidence call does not mean a 75% probability of winning.</strong> Model confidence is
        notoriously miscalibrated. The whole point of the <Link to="/calibration">Calibration dashboard</Link> is to
        show you what those confidence numbers <em>actually</em> translate to in realized win rate — so you can discount
        or trust accordingly. On a fresh account we show community-wide aggregate stats until you have enough personal
        history for your own numbers to matter.
      </div>

      <h2>What to do with a Screener result</h2>
      <ol>
        <li>
          <strong>Read the reason, not just the direction.</strong> If the reason cites three confirming signals plus
          regime alignment, treat it differently than one that says "RSI oversold."
        </li>
        <li>
          <strong>Check the session phase badge.</strong> A LUNCH-hour signal has a lower bar than a MID_MORNING one —
          the model knows this and factored it in, but it's worth your own look.
        </li>
        <li>
          <strong>Use the "One-click trade" button if you're going to act.</strong> It carries the AI's stop/target into
          QuickTrade so the thesis and the exit plan travel together.
        </li>
      </ol>

      <div className={styles.callout}>
        <strong>Why this design:</strong> most losses come from taking mediocre setups that feel like trades, not from
        missing great setups that required conviction. Dual-AI consensus is primarily a <em>filter</em>, not a
        generator. It's designed to reject.
      </div>
    </LearnLayout>
  );
};

export default AiScreener;
